import React, { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, LogOut, Loader2, BookOpen, GraduationCap, Settings, Upload } from "lucide-react";
import type { Question } from "@/types/quiz";
import SubjectSelector from "@/components/quiz/SubjectSelector";
import QuestionCard from "@/components/quiz/QuestionCard";
import Results from "@/components/quiz/Results";
import EnhancedResults from "@/components/quiz/EnhancedResults";
import QuizLoading from "@/components/quiz/QuizLoading";
import { useQuiz } from "@/hooks/useQuiz";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import CategorySelection from "./quiz/CategorySelection";
import { getSubjects } from "@/data/questions";
import type { UserAchievements } from "@/data/achievements";
import { useNavigate } from "react-router-dom";
import { useAuthSession } from "@/hooks/useAuthSession";
import { useQuizHints } from "@/hooks/useQuizHints";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuizState } from "@/hooks/useQuizState";
import QuizAikenImport from "@/components/quiz/QuizAikenImport";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AnkiImport from "@/components/quiz/AnkiImport";

interface QuizProps {
  subjects?: string[];
}

const Quiz: React.FC<QuizProps> = ({ subjects = [] }) => {
  const { 
    state, 
    setState,
    questions,
    questionsLoading: loading,
    error,
    handleSubjectSelect: originalHandleSubjectSelect,
    handleResetProgress: handleReset,
    handleNext: originalHandleNext,
    availableCategories,
    categories,
    adaptiveDifficulty,
    setCompletedQuestions,
    initialState,
    setView,
    setQuestions // Import this for refreshing questions
  } = useQuiz();
  
  const { session } = useAuthSession();
  const navigate = useNavigate();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<'english' | 'hindi' | 'hinglish'>('english');
  const [isInputLocked, setIsInputLocked] = useState(false);
  const lockTimerRef = useRef<number | null>(null);
  
  const { handleShowHint, isLoadingHint } = useQuizHints(
    state, 
    setState, 
    questions
  );

  const allSnapshotsRef = useRef<Question[]>([]);
  const [importTab, setImportTab] = useState<string>("anki");

  React.useEffect(() => {
    if (state.view === "results") {
      const globalSnapshots = (window as any).questionSnapshots?.current || [];
      console.log("Global snapshots at results view:", globalSnapshots.length);
      
      if (globalSnapshots.length > 0 && (!state.completedQuestions?.length || state.completedQuestions.length < globalSnapshots.length)) {
        console.log("Setting completed questions from snapshots:", globalSnapshots.length);
        const sortedSnapshots = [...globalSnapshots].sort((a, b) => 
          (a.shownAtIndex !== undefined && b.shownAtIndex !== undefined) 
            ? a.shownAtIndex - b.shownAtIndex 
            : 0
        );
        setCompletedQuestions(sortedSnapshots);
      } else if (questions.length > 0 && !state.completedQuestions?.length) {
        console.log("Storing completed questions from questions array:", questions.length);
        setCompletedQuestions(questions);
      }
    }
  }, [state.view, questions, state.completedQuestions, setCompletedQuestions]);

  React.useEffect(() => {
    console.log("Current view state:", state.view);
    console.log("Current subject:", state.subject);
    console.log("Questions loaded:", questions.length);
    console.log("Current MAX_QUESTIONS setting:", state.questionsCount);
    console.log("Current question index:", state.questionIndex);
  }, [state.view, state.subject, questions.length, state.questionsCount, state.questionIndex]);

  const handleAnswer = useCallback((selected: number) => {
    console.log("Answer selected in Quiz component:", selected);

    if (isTransitioning || isInputLocked) {
      console.log("Preventing answer during transition or input lock");
      return;
    }
    
    setIsTransitioning(true);
    setIsInputLocked(true);

    setState(prev => {
      const newAnswers = [
        ...prev.answers.slice(0, prev.questionIndex),
        selected,
        ...prev.answers.slice(prev.questionIndex + 1)
      ];
      
      return {
        ...prev,
        answers: newAnswers,
        selectedAnswer: selected
      };
    });
    
    setTimeout(() => {
      setIsTransitioning(false);
      setIsInputLocked(false);
    }, 300);
    
  }, [setState, isTransitioning, isInputLocked]);

  const handleNext = useCallback(() => {
    if (isTransitioning || isInputLocked) {
      console.log("Preventing next during transition or input lock");
      return;
    }
    
    setIsTransitioning(true);
    setIsInputLocked(true);
    console.log("Input locked for next question transition");

    if ((window as any).questionSnapshots?.current) {
      allSnapshotsRef.current = [...(window as any).questionSnapshots.current];
      console.log("Updated local snapshots before moving to next:", allSnapshotsRef.current.length);
    }
    
    if (lockTimerRef.current) {
      window.clearTimeout(lockTimerRef.current);
      lockTimerRef.current = null;
    }
    
    setTimeout(() => {
      originalHandleNext();
      setIsTransitioning(false);
      
      lockTimerRef.current = window.setTimeout(() => {
        setIsInputLocked(false);
        console.log("Input unlocked after new question loaded");
      }, 5000);
    }, 300);
  }, [originalHandleNext, isTransitioning, isInputLocked]);

  // Define a refreshQuestions function since it doesn't exist in useQuiz
  const refreshQuestions = useCallback(() => {
    // If you have a subject selected, refresh questions for that subject
    if (state.subject) {
      // This is a simplified approach - just reset the questions array
      // and let the existing mechanisms reload them
      setQuestions([]);
      toast.info("Refreshing questions...");
      // Re-select the current subject to trigger a refresh
      originalHandleSubjectSelect(state.subject);
    }
  }, [state.subject, originalHandleSubjectSelect, setQuestions]);

  const handleImport = () => {
    toast.success("Questions imported successfully");
    refreshQuestions();
  };

  const handleBackToSubjects = () => {
    setState(prev => ({ 
      ...prev, 
      view: "subject-selection",
      subject: null,
      questionIndex: 0,
      answers: [],
      showHint: false,
      currentHint: null
    }));
    toast.info("Returned to subject selection");
  };

  const handleCategorySelect = (category: string, categoryId: number) => {
    setState(prev => ({
      ...prev,
      category,
      view: "subject-selection",
      showHint: false,
      currentHint: null
    }));
    toast.success(`Selected category: ${category}`);
  };
  
  const handleLogout = () => {
    toast.info("Logging out...");
    navigate("/auth");
  };

  React.useEffect(() => {
    console.log(`Current difficulty: ${state.difficulty}`);
  }, [state.difficulty]);

  if (loading) {
    return <QuizLoading />;
  }

  if (error || (state.view === "quiz" && (!questions || questions.length === 0))) {
    return (
      <Card className="max-w-2xl mx-auto p-8 bg-white shadow-lg rounded-xl">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="p-4 rounded-full bg-red-100">
            <Loader2 className="h-8 w-8 text-red-500" />
          </div>
          <div className="text-center space-y-2">
            <h3 className="text-xl font-semibold text-red-600">
              Failed to load questions
            </h3>
            <p className="text-gray-600 max-w-md mx-auto">
              {error?.message || "No questions available for this subject."}
            </p>
          </div>
          <Button
            variant="default"
            onClick={handleBackToSubjects}
            className="mt-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Subject Selection
          </Button>
        </div>
      </Card>
    );
  }

  if (!state.category) {
    const emptyUserAchievements: UserAchievements = {
      speed_demon: { earned: false },
      perfect_streak: { earned: false },
      subject_explorer: { earned: false },
      master_mind: { earned: false },
      challenge_seeker: { earned: false },
      flawless_victory: { earned: false }
    };
    
    return (
      <div className="px-4 py-6 max-w-4xl mx-auto">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-gradient-to-r from-purple-100 to-blue-100">
                <GraduationCap className="h-6 w-6 text-primary" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Quiz Assessment</h1>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200 active:bg-red-100 active:scale-95 transition-all"
            >
              <LogOut className="h-4 w-4 mr-1" />
              Logout
            </Button>
          </div>
          <p className="text-gray-600 ml-1">Select a category to begin your knowledge assessment</p>
        </div>
        
        <CategorySelection 
          categories={availableCategories}
          onCategorySelect={handleCategorySelect}
          userAchievements={emptyUserAchievements}
          onResetProgress={handleReset}
        />
      </div>
    );
  }

  if (state.view === "subject-selection") {
    const categorySubjects = state.category ? getSubjects(state.category) : [];
    
    return (
      <div className="px-4 py-6 max-w-4xl mx-auto">
        <div className="flex items-center mb-6">
          <Button
            variant="ghost"
            onClick={() => setState(prev => ({ ...prev, category: null }))}
            className="mr-2"
            size="sm"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-primary/10">
              <BookOpen className="h-5 w-5 text-primary" />
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-800">
              <span className="text-primary">{state.category}</span> Subjects
            </h1>
          </div>
        </div>
        
        <SubjectSelector
          subjects={state.category ? getSubjects(state.category) : []}
          onSubjectSelect={originalHandleSubjectSelect}
          onReset={handleReset}
        />
        
        {state.subject && (
          <Card className="mt-8 border border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Import Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={importTab} onValueChange={setImportTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="anki">Anki Format</TabsTrigger>
                  <TabsTrigger value="aiken">Aiken Format</TabsTrigger>
                </TabsList>
                <TabsContent value="anki">
                  <div className="mb-2 text-sm text-gray-600">
                    Import questions from Anki format for {state.subject}
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4 text-sm text-amber-800">
                    <strong>Note:</strong> Import from Anki format will add flashcards to your collection.
                  </div>
                  <AnkiImport 
                    subject={state.subject} 
                    onImport={handleImport}
                  />
                </TabsContent>
                <TabsContent value="aiken">
                  <div className="mb-2 text-sm text-gray-600">
                    Import questions from Aiken format for {state.subject}
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4 text-sm text-amber-800">
                    <strong>Important:</strong> Aiken format imports quiz questions for {state.subject} subject.
                    <p className="mt-1">Format: Question, followed by options (A. Option), then ANSWER: A</p>
                  </div>
                  <QuizAikenImport 
                    subject={state.subject} 
                    onImport={handleImport}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  if (state.view === "quiz") {
    console.log("Rendering quiz questions. Current question index:", state.questionIndex);
    console.log("Current questions array:", questions);
    
    const currentAnswer = state.answers[state.questionIndex] !== undefined 
      ? state.answers[state.questionIndex] 
      : null;
      
    const currentQuestion = questions[state.questionIndex];
    const questionDifficulty = currentQuestion?.difficulty || state.difficulty;
    
    console.log("Current question:", currentQuestion);
    console.log("Current answer:", currentAnswer);
    console.log("Current input lock state:", isInputLocked);
    
    if (!currentQuestion) {
      console.error("No question found at index", state.questionIndex);
      return (
        <Card className="max-w-2xl mx-auto p-8 bg-white shadow-lg rounded-xl">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="p-4 rounded-full bg-amber-100">
              <Loader2 className="h-8 w-8 text-amber-500" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-xl font-semibold text-amber-600">
                Question not available
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Could not find the current question. Please try selecting the subject again.
              </p>
            </div>
            <Button
              variant="default"
              onClick={handleBackToSubjects}
              className="mt-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Subject Selection
            </Button>
          </div>
        </Card>
      );
    }
    
    return (
      <div className="p-4 max-w-4xl mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={handleBackToSubjects}
            size="sm"
            className="text-gray-600 hover:text-gray-900 h-9 px-3"
            disabled={isTransitioning || isInputLocked}
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="text-xs py-1 px-2.5 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center gap-1.5">
              <Settings className="h-3 w-3 text-gray-500" /> 
              <span>Difficulty:</span>
              <span className={`font-semibold ${
                questionDifficulty === 'easy' ? 'text-green-600' : 
                questionDifficulty === 'medium' ? 'text-amber-600' : 
                'text-red-600'
              }`}>
                {questionDifficulty?.charAt(0).toUpperCase() + questionDifficulty?.slice(1)}
              </span>
            </div>
            
            <Select
              value={selectedLanguage}
              onValueChange={(value: 'english' | 'hindi' | 'hinglish') => setSelectedLanguage(value)}
              disabled={isTransitioning || isInputLocked}
            >
              <SelectTrigger className="w-[130px] h-9 text-xs">
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="english">English</SelectItem>
                <SelectItem value="hindi">हिंदी</SelectItem>
                <SelectItem value="hinglish">Hinglish</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex items-center gap-2 mb-6">
          <div className="p-1.5 rounded-full bg-primary/10">
            <BookOpen className="h-4 w-4 text-primary" />
          </div>
          <h1 className="text-lg font-semibold text-gray-800">
            <span className="text-primary">{state.category}</span> 
            <span className="mx-1.5 text-gray-400">•</span> 
            <span>{state.subject}</span>
          </h1>
        </div>
        
        <QuestionCard
          question={currentQuestion}
          questionNumber={state.questionIndex + 1}
          totalQuestions={questions.length}
          selectedAnswer={currentAnswer}
          onSelect={handleAnswer}
          onNext={handleNext}
          selectedLanguage={selectedLanguage}
          showBilingual={state.subject !== 'English'}
          onShowHint={handleShowHint}
          isLoadingHint={isLoadingHint}
          currentHint={state.showHint ? state.currentHint : null}
          isTransitioning={isTransitioning}
          isInputLocked={isInputLocked}
        />
      </div>
    );
  }

  if (state.view === "results") {
    const resultsQuestions = allSnapshotsRef.current.length > 0 
      ? allSnapshotsRef.current 
      : state.completedQuestions?.length 
        ? state.completedQuestions 
        : questions;
    
    console.log("Rendering results with questions:", resultsQuestions.length);
    console.log("And answers:", state.answers.length);
    console.log("Session ID for results:", state.lastSessionId);
    
    return (
      <div className="p-4 max-w-4xl mx-auto">
        <Button
          variant="outline"
          onClick={handleBackToSubjects}
          className="mb-6"
          size="sm"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Subjects
        </Button>
        
        <Results 
          questions={resultsQuestions} 
          answers={state.answers.filter(answer => answer !== null)} 
          onReset={() => setState({...initialState})} 
          sessionId={state.lastSessionId}
          subject={state.subject}
          category={state.category}
          difficulty={state.difficulty}
        />
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-[50vh] p-8">
      <Card className="p-8 text-center bg-white shadow-lg rounded-xl">
        <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-4" />
        <p className="text-gray-700">Something went wrong. Please try again.</p>
      </Card>
    </div>
  );
};

export default Quiz;
